import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment/moment";
import { ThreeDots } from "react-loader-spinner";
import axios from ".././axios-interceptor";
import { base_url, LOAN_CURRENCY_TYPE_USD } from "./../utilities";

// Read-only audit trail of admin edits to an investor's amount_transferred
// (investor-master/get_balance_audit_by_investor). Rows arrive newest first.
const BalanceHistoryModal = ({ show, onHide, investor }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const investorId = investor?.investor_id;
  // AdminInvestors.exportToExcel rewrites the shared rows' currency_type from 'USD' to '$', so accept both.
  const isUsd = investor?.currency === LOAN_CURRENCY_TYPE_USD || investor?.currency === "$";

  useEffect(() => {
    if (!show || !investorId) return;
    let cancelled = false;
    setRows([]);
    setLoading(true);
    axios
      .post(base_url + "investor-master/get_balance_audit_by_investor", { investor_id: investorId })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status === 200) setRows(Array.isArray(res.data.data) ? res.data.data : []);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, investorId]);

  const money = (n) =>
    `${isUsd ? "$\u00A0" : "BHD "}${Number(n || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 500 }}>
          Balance history
          {investor?.investor_name || investor?.investor_reference_no ? (
            <span style={{ fontSize: 12.5, color: "#7C8495", fontWeight: 400, marginLeft: 8 }}>
              {[investor?.investor_name, investor?.investor_reference_no].filter(Boolean).join(" · ")}
            </span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div className="d-flex justify-content-center mt-3">
            <ThreeDots height="40" width="40" radius="9" color="#3D79F3" ariaLabel="three-dots-loading" visible={true} />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center" style={{ fontSize: 13, color: "#7C8495", padding: "18px 0" }}>
            No balance changes recorded
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table table-striped" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Changed by</th>
                  <th>Previous → New</th>
                  <th>Change</th>
                  <th>Payment reference</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const delta = Number(r.amount ?? Number(r.new_balance) - Number(r.previous_balance));
                  return (
                    <tr key={r.audit_id || r._id}>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {r.created_date_time ? moment(Number(r.created_date_time)).format("DD MMM YYYY, HH:mm") : "-"}
                      </td>
                      <td>{r.admin_user_name || "-"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {money(r.previous_balance)} → {money(r.new_balance)}
                      </td>
                      <td
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                          color: delta > 0 ? "#3B6D11" : delta < 0 ? "#A32D2D" : "#7C8495",
                        }}
                      >
                        {delta > 0 ? "+" : delta < 0 ? "−" : ""}
                        {money(Math.abs(delta))}
                      </td>
                      <td style={{ wordBreak: "break-word" }}>{r.payment_reference || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p style={{ fontSize: 11, color: "#7C8495", marginBottom: 0 }}>
          Every admin change to this investor's deposit balance, with who made it and the payment reference.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn_background px-4" onClick={onHide}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default BalanceHistoryModal;
